/**
 * Leviathan Web - Net vs Gross Reconciliation Report
 * Quasar Prime LLC
 * Aggressive Tactical Industrial Design
 *
 * Shows variance between gross (ambient) and net (60F corrected) gallon volumes
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  ArrowLeft,
  RefreshCw,
  Printer,
  Thermometer,
  Droplets,
  TrendingDown,
  DollarSign,
} from 'lucide-react';
import { API_URL } from '@/config/api';

interface ReconciliationRow {
  id: string;
  shipmentId: string;
  shipmentNumber: string;
  shipDate: string;
  customer: { id: string; name: string; customerNumber: string };
  product: { id: string; name: string; sku: string };
  grossQuantity: number;
  netQuantity: number;
  temperatureF: number | null;
  conversionFactor: number | null;
  variance: number;
  variancePercent: number;
  unitPrice: number;
  dollarImpact: number;
}

interface ReconciliationSummary {
  totalGross: number;
  totalNet: number;
  totalVariance: number;
  variancePercent: number;
  avgConversionFactor: number | null;
  totalDollarImpact: number;
  itemCount: number;
}

interface ReconciliationData {
  rows: ReconciliationRow[];
  summary: ReconciliationSummary;
}

function StatCard({ label, value, icon }: { label: string; value: string; icon: React.ReactNode }) {
  return (
    <div className="relative bg-panel-dark border border-panel-border p-4">
      <div className="absolute top-0 left-0 w-2 h-2 border-l border-t border-amber" />
      <div className="absolute top-0 right-0 w-2 h-2 border-r border-t border-amber" />
      <div className="absolute bottom-0 left-0 w-2 h-2 border-l border-b border-amber" />
      <div className="absolute bottom-0 right-0 w-2 h-2 border-r border-b border-amber" />
      <div className="flex items-center space-x-2 mb-2">
        <span className="text-amber">{icon}</span>
        <span className="text-text-tertiary text-xs uppercase tracking-widest font-mono">{label}</span>
      </div>
      <div className="text-xl font-mono font-bold text-amber" style={{ textShadow: '0 0 10px rgba(255,149,0,0.6)' }}>
        {value}
      </div>
    </div>
  );
}

const ReconciliationReport = () => {
  const { accessToken } = useAuth();
  const [data, setData] = useState<ReconciliationData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Default to current month
  const now = new Date();
  const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
  const today = now.toISOString().split('T')[0];

  const [startDate, setStartDate] = useState(firstOfMonth);
  const [endDate, setEndDate] = useState(today);

  const fetchReport = async () => {
    setIsLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ startDate: startDate || '', endDate: endDate || '' });
      const response = await fetch(`${API_URL}/api/reports/reconciliation?${params}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.ok) {
        setData(await response.json());
      } else {
        setError('Failed to load reconciliation report');
      }
    } catch {
      setError('Failed to load report');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (startDate && endDate) fetchReport();
  }, []);

  const formatNumber = (n: number, decimals = 2) =>
    n.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  const formatCurrency = (n: number) => `$${formatNumber(n)}`;
  const formatDate = (d: string) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link to="/reports" className="text-text-secondary hover:text-amber transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-display font-bold text-text-primary uppercase tracking-wider">
              Net vs Gross Reconciliation
            </h1>
            <p className="text-text-tertiary text-sm font-mono">Temperature variance analysis</p>
          </div>
        </div>
        <button onClick={() => window.print()} className="flex items-center space-x-2 px-4 py-2 bg-panel-dark hover:bg-steel/20 text-text-secondary hover:text-steel border border-panel-border transition-all no-print">
          <Printer className="w-4 h-4" />
          <span className="text-xs font-mono uppercase">Print</span>
        </button>
      </div>

      {/* Filters */}
      <div className="flex items-center space-x-4 no-print">
        <div>
          <label className="block text-xs font-mono text-text-tertiary uppercase mb-1">Start Date</label>
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="bg-panel-dark border border-panel-border text-text-primary px-3 py-2 font-mono text-sm focus:border-amber focus:outline-none" />
        </div>
        <div>
          <label className="block text-xs font-mono text-text-tertiary uppercase mb-1">End Date</label>
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="bg-panel-dark border border-panel-border text-text-primary px-3 py-2 font-mono text-sm focus:border-amber focus:outline-none" />
        </div>
        <div className="flex items-end">
          <button onClick={fetchReport} disabled={isLoading} className="flex items-center space-x-2 px-6 py-2 bg-amber hover:bg-amber/90 text-background font-display font-bold uppercase tracking-wider transition-all disabled:opacity-50">
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            <span>Generate</span>
          </button>
        </div>
      </div>

      {error && <div className="bg-error-red/20 border border-error-red/50 text-error-red px-4 py-3 font-mono text-sm">{error}</div>}

      {data && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <StatCard label="Total Gross" value={`${formatNumber(data.summary.totalGross)} gal`} icon={<Droplets className="w-4 h-4" />} />
            <StatCard label="Total Net" value={`${formatNumber(data.summary.totalNet)} gal`} icon={<Droplets className="w-4 h-4" />} />
            <StatCard label="Variance" value={`${formatNumber(data.summary.totalVariance)} gal`} icon={<TrendingDown className="w-4 h-4" />} />
            <StatCard label="Variance %" value={`${data.summary.variancePercent}%`} icon={<TrendingDown className="w-4 h-4" />} />
            <StatCard label="Avg Factor" value={data.summary.avgConversionFactor ? data.summary.avgConversionFactor.toFixed(6) : 'N/A'} icon={<Thermometer className="w-4 h-4" />} />
            <StatCard label="Dollar Impact" value={formatCurrency(data.summary.totalDollarImpact)} icon={<DollarSign className="w-4 h-4" />} />
          </div>

          {/* Data Table */}
          <div className="relative bg-panel-dark border border-panel-border overflow-x-auto">
            <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
            <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
            <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
            <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-panel-border bg-background/50">
                  <th className="text-left px-4 py-3 text-text-tertiary font-mono text-xs uppercase tracking-wider">Shipment</th>
                  <th className="text-left px-4 py-3 text-text-tertiary font-mono text-xs uppercase tracking-wider">Date</th>
                  <th className="text-left px-4 py-3 text-text-tertiary font-mono text-xs uppercase tracking-wider">Customer</th>
                  <th className="text-left px-4 py-3 text-text-tertiary font-mono text-xs uppercase tracking-wider">Product</th>
                  <th className="text-right px-4 py-3 text-text-tertiary font-mono text-xs uppercase tracking-wider">Gross (gal)</th>
                  <th className="text-right px-4 py-3 text-text-tertiary font-mono text-xs uppercase tracking-wider">Net (gal)</th>
                  <th className="text-right px-4 py-3 text-text-tertiary font-mono text-xs uppercase tracking-wider">Temp (F)</th>
                  <th className="text-right px-4 py-3 text-text-tertiary font-mono text-xs uppercase tracking-wider">Factor</th>
                  <th className="text-right px-4 py-3 text-text-tertiary font-mono text-xs uppercase tracking-wider">Variance</th>
                  <th className="text-right px-4 py-3 text-text-tertiary font-mono text-xs uppercase tracking-wider">Var %</th>
                  <th className="text-right px-4 py-3 text-text-tertiary font-mono text-xs uppercase tracking-wider">$ Impact</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="px-4 py-8 text-center text-text-tertiary font-mono">No reconciliation data found for this period</td>
                  </tr>
                ) : (
                  data.rows.map((row) => (
                    <tr key={row.id} className="border-b border-panel-border/50 hover:bg-amber/5 transition-colors">
                      <td className="px-4 py-3">
                        <Link to={`/shipments/${row.shipmentId}`} className="text-steel hover:text-amber font-mono text-xs">{row.shipmentNumber}</Link>
                      </td>
                      <td className="px-4 py-3 text-text-secondary font-mono text-xs">{formatDate(row.shipDate)}</td>
                      <td className="px-4 py-3 text-text-primary text-xs">{row.customer.name}</td>
                      <td className="px-4 py-3 text-text-secondary font-mono text-xs">{row.product.sku}</td>
                      <td className="px-4 py-3 text-right text-text-primary font-mono text-xs">{formatNumber(row.grossQuantity)}</td>
                      <td className="px-4 py-3 text-right text-text-primary font-mono text-xs">{formatNumber(row.netQuantity)}</td>
                      <td className="px-4 py-3 text-right text-text-secondary font-mono text-xs">{row.temperatureF != null ? `${row.temperatureF}°` : '-'}</td>
                      <td className="px-4 py-3 text-right text-text-secondary font-mono text-xs">{row.conversionFactor != null ? row.conversionFactor.toFixed(6) : '-'}</td>
                      <td className="px-4 py-3 text-right font-mono text-xs">
                        <span className={row.variance > 0 ? 'text-warning-amber' : 'text-success-green'}>{formatNumber(row.variance)}</span>
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-xs">
                        <span className={row.variancePercent > 2 ? 'text-error-red' : 'text-text-secondary'}>{row.variancePercent}%</span>
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-xs text-warning-amber">{formatCurrency(row.dollarImpact)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default ReconciliationReport;
